import React from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';

const HeaderContainer = styled(motion.header)`
  padding: 60px 20px 40px;
  text-align: center;
  position: relative;
  z-index: 2;
  overflow: hidden;
  
  @media (max-width: 768px) {
    padding: 40px 15px 30px;
  }
  
  @media (max-width: 480px) {
    padding: 30px 10px 20px;
  }
`;

const HeaderContent = styled(motion.div)`
  position: relative;
  max-width: 900px;
  margin: 0 auto;
  padding: 2rem;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  box-shadow: 0 15px 35px rgba(255, 105, 180, 0.15);
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 20px;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
    border-radius: 16px;
  }
`;

const Title = styled(motion.h1)`
  font-size: 3.8rem;
  font-weight: 900;
  margin-bottom: 1.5rem;
  background: linear-gradient(45deg, #ff1493, #ff69b4, #da70d6, #ff1493);
  background-size: 300% 300%;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  text-shadow: 3px 3px 10px rgba(255, 105, 180, 0.3);
  line-height: 1.2;
  animation: gradient 8s ease infinite;

  @keyframes gradient {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }

  @media (max-width: 768px) {
    font-size: 2.8rem;
    margin-bottom: 1rem;
  }
  
  @media (max-width: 480px) {
    font-size: 2.2rem;
  }
`;

const Subtitle = styled(motion.h2)`
  font-size: 1.5rem;
  font-weight: 500;
  color: #d84079;
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.8;
  text-shadow: 1px 1px 3px rgba(255, 255, 255, 0.8);

  @media (max-width: 768px) {
    font-size: 1.3rem;
    line-height: 1.6;
  }
  
  @media (max-width: 480px) {
    font-size: 1.1rem;
    line-height: 1.5;
  }
`;

const FloatingHeart = styled(motion.div)`
  position: absolute;
  font-size: ${props => props.size || '3rem'};
  color: ${props => props.color || '#ff4b8d'};
  filter: drop-shadow(0 0 10px ${props => props.shadowColor || 'rgba(255, 75, 141, 0.5)'});
  z-index: ${props => props.zIndex || '1'};
  opacity: ${props => props.opacity || '1'};
  
  @media (max-width: 768px) {
    font-size: ${props => props.sizeMobile || '2.5rem'};
  }
  
  @media (max-width: 480px) {
    font-size: ${props => props.sizeSmallMobile || '2rem'};
  }
`;

const containerVariants = {
  hidden: { 
    opacity: 0 
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.3,
      delayChildren: 0.2
    }
  }
};

const itemVariants = {
  hidden: { 
    y: -30, 
    opacity: 0 
  },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 10
    }
  }
};

// Array of floating hearts with different positions and animations
const floatingHearts = [
  {
    id: 1,
    emoji: '💖',
    initial: { x: -150, y: -50 },
    animate: {
      x: [-150, -140, -150],
      y: [-50, -70, -50],
      rotate: [0, 15, 0]
    },
    transition: { duration: 4, repeat: Infinity, repeatType: "reverse" },
    size: '3.2rem',
    sizeMobile: '2.6rem',
    sizeSmallMobile: '2.1rem',
    color: '#ff4b8d'
  },
  {
    id: 2,
    emoji: '💕',
    initial: { x: 150, y: -30 },
    animate: {
      x: [150, 160, 150],
      y: [-30, -50, -30],
      rotate: [0, -15, 0]
    },
    transition: { duration: 3.5, repeat: Infinity, repeatType: "reverse", delay: 0.5 },
    size: '3rem',
    sizeMobile: '2.4rem',
    sizeSmallMobile: '2rem',
    color: '#ff69b4'
  },
  {
    id: 3,
    emoji: '💓',
    initial: { x: -180, y: 120 },
    animate: {
      x: [-180, -170, -180],
      y: [120, 130, 120],
      rotate: [0, 10, 0]
    },
    transition: { duration: 5, repeat: Infinity, repeatType: "reverse", delay: 1 },
    size: '2.5rem',
    sizeMobile: '2rem',
    sizeSmallMobile: '1.7rem',
    color: '#da70d6'
  },
  {
    id: 4,
    emoji: '❤️',
    initial: { x: 200, y: 100 },
    animate: {
      x: [200, 210, 200],
      y: [100, 110, 100],
      rotate: [0, -10, 0]
    },
    transition: { duration: 4.5, repeat: Infinity, repeatType: "reverse", delay: 1.5 },
    size: '2.7rem',
    sizeMobile: '2.2rem',
    sizeSmallMobile: '1.8rem',
    color: '#ff1493'
  },
  {
    id: 5,
    emoji: '💗',
    initial: { x: 0, y: -90 },
    animate: {
      x: [0, 10, 0],
      y: [-90, -100, -90],
      scale: [1, 1.1, 1],
      rotate: [0, 5, 0]
    },
    transition: { duration: 3, repeat: Infinity, repeatType: "reverse", delay: 2 },
    size: '3.5rem',
    sizeMobile: '2.8rem',
    sizeSmallMobile: '2.3rem',
    color: '#ff85a2'
  }
];

const Header = () => {
  return (
    <HeaderContainer
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {floatingHearts.map(heart => (
        <FloatingHeart
          key={heart.id}
          initial={heart.initial}
          animate={heart.animate}
          transition={heart.transition}
          size={heart.size}
          sizeMobile={heart.sizeMobile}
          sizeSmallMobile={heart.sizeSmallMobile}
          color={heart.color}
        >
          {heart.emoji}
        </FloatingHeart>
      ))}
      
      <HeaderContent
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <Title 
          variants={itemVariants}
          animate={{
            scale: [1, 1.03, 1],
            textShadow: [
              "3px 3px 10px rgba(255, 105, 180, 0.3)",
              "3px 3px 15px rgba(255, 105, 180, 0.6)",
              "3px 3px 10px rgba(255, 105, 180, 0.3)"
            ]
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        >
          برای دلآرام زیبای من
        </Title>
        <Subtitle variants={itemVariants}>
          تو زیباترین هدیه‌ای هستی که زندگی به من داده است. در کنارت بودن، بزرگترین خوشبختی من است. 
          امیدوارم این صفحه کوچک لبخند به لبانت بیاورد و بدانی که همیشه و در هر شرایطی دوستت دارم.
        </Subtitle>
      </HeaderContent>
    </HeaderContainer>
  );
};

export default Header; 